import { VFC } from "react";

import { gql, TypedDocumentNode, useMutation } from "@apollo/client";
import { Business, SyncAlt } from "@material-ui/icons";
import { Avatar, TextInput } from "@portex-pro/ui-components";
import { skipToken } from "@reduxjs/toolkit/dist/query";
import { Contact, MutationUpdatePartnerArgs } from "api/graphql/generated";
import { useCreateAddressMutation } from "api/rest/address/createAddressApi";
import { useGetAddressQuery } from "api/rest/address/getAddressApi";
import { useUpdateAddressMutation } from "api/rest/address/updateAddressApi";
import { useAssociatePartnerTagsMutation, useCreatePartnerRequestMutation } from "api/rest/partners/partnersApi";
import { PaymentPayer, PaymentTerms, Smc3Currency } from "api/rest/smc3/types";
import { useUpsertCarrierSmc3Mutation } from "api/rest/smc3/upsertCarrierSmc3";
import { Mode } from "api/types/generated-types";
import portexIcon from "assets/portex-icon.png";
import PortexDialog from "components/PortexDialog";
import Text from "components/Text";
import { useOnApolloError } from "hooks/useOnApolloError";
import { useSnackbar } from "notistack";
import FormSelectView from "pages/shipper/pages/request-quote/components/FormSelectView";
import { useTranslation } from "react-i18next";
import { Sentry } from "sentry";
import { validateAddress } from "utils/addresses/validateAddress";
import { validateEmail } from "utils/validateEmail";
import AddressForm from "views/addresses/AddressForm";

import { usePartnersSliceSelector } from "../store/partnersStore";
import { useResetDialogState, useSetState } from "../store/smc3IntegrationsSlice";

const TAG = gql`
  fragment PartnersPage_Tag on Tag {
    id
    tag
    config {
      group
      color
    }
  }
`;

const UPDATE_CONTACT_RESPONSE = gql`
  fragment PartnersPage_Update_Contact on Contact {
    id
    first_name
    last_name
    company_name
    phone_number
    city
    state
    created_at
    tags {
      ...PartnersPage_Tag
    }
  }
  ${TAG}
`;

type ContactFragmentFields =
  | "id"
  | "user"
  | "first_name"
  | "last_name"
  | "company_name"
  | "phone_number"
  | "city"
  | "state";

type PickedUpdateContact = Pick<Contact, ContactFragmentFields>;

const UPDATE_CONTACT: TypedDocumentNode<{ updatePartner: PickedUpdateContact }, MutationUpdatePartnerArgs> = gql`
  mutation ($input: UpdatePartnerInput!) {
    updatePartner(input: $input) {
      ...PartnersPage_Update_Contact
    }
  }
  ${UPDATE_CONTACT_RESPONSE}
`;

const CarrierIntegrationDialogContainer: VFC = () => {
  const {
    dialogOpen: open,
    email,
    firstName,
    key,
    lastName,
    password,
    securityCode,
    secret,
    trackingSecret,
    selectedCarrier: carrier,
    username,
    billToAccountNumber,
    billToAddressId,
    paymentCurrency,
    paymentPayer,
    paymentTerms,
    billingName,
    address,
    isCreated,
  } = usePartnersSliceSelector((store) => store.smc3IntegrationsSlice);
  const setState = useSetState();
  const resetState = useResetDialogState();
  const { onApolloError } = useOnApolloError({ componentName: "PartnersPage" });

  const { isLoading: isLoadingAddress } = useGetAddressQuery(
    billToAddressId ? { urlParams: { addressId: billToAddressId.toString() } } : skipToken
  );

  const [createPartner] = useCreatePartnerRequestMutation();
  const [associateTags] = useAssociatePartnerTagsMutation();
  const [upsertCarrier] = useUpsertCarrierSmc3Mutation();
  const [createAddress] = useCreateAddressMutation();
  const [updateAddress] = useUpdateAddressMutation();
  const [updatePartner] = useMutation(UPDATE_CONTACT, {
    onError: onApolloError("updatePartner"),
  });

  const { t } = useTranslation(["common", "shipper"]);
  const { enqueueSnackbar } = useSnackbar();

  const handleConnect = async () => {
    if (!carrier || !email || !validateEmail(email)) {
      return;
    }

    try {
      let partnerId;
      if (!isCreated || carrier.smc3CarrierShipper?.contact?.user?.email !== email) {
        const createdPartner = await createPartner({
          company_name: carrier?.carrier.name,
          email: email,
          first_name: firstName,
          last_name: lastName,
        }).unwrap();

        await associateTags({
          queryParams: { partnerId: createdPartner.data.partner.id },
          body: { tags: ["LTL Auto Pricing"] },
        }).unwrap();

        partnerId = createdPartner.data.partner.id;
      } else {
        if (!!carrier.smc3CarrierShipper?.contact.id) {
          await updatePartner({
            variables: {
              input: {
                id: carrier.smc3CarrierShipper.contact.id.toString(),
                first_name: firstName,
                last_name: lastName,
              },
            },
          });
        }
        partnerId = carrier.smc3CarrierShipper.contact.id;
      }

      let addressId;

      if (!!address.id) {
        const updatedAddress = await updateAddress({ body: address, urlParams: { addressId: address.id } }).unwrap();
        addressId = updatedAddress.data.address.id;
      } else {
        const createdAddress = await createAddress({ body: address }).unwrap();
        addressId = createdAddress.data.address.id;
      }

      await upsertCarrier({
        body: {
          credentials:
            isCreated && !username && !password && !securityCode && !key && !secret && !trackingSecret
              ? undefined
              : { username, password, securityCode, key, secret, trackingSecret },
          contactId: partnerId,
          billingAddressId: addressId,
          billingAccountNumber: billToAccountNumber,
          billingName: billingName,
          paymentCurrency,
          paymentPayer,
          paymentTerms,
        },
        urlParams: { scac: carrier.carrier.scac },
      }).unwrap();
    } catch (e) {
      enqueueSnackbar(t("common:errors.generic"), { variant: "error" });
      Sentry.captureException(e);
    } finally {
      setState({ dialogOpen: false });
    }
  };

  return (
    <PortexDialog
      open={open}
      title={t("common:connectWith", { carrier: carrier?.carrier.name })}
      fullWidth
      onClose={() => {
        resetState();
      }}
      confirmButtonProps={{
        disabled:
          (!username && carrier?.creds_username_required && !isCreated) ||
          (!password && carrier?.creds_password_required && !isCreated) ||
          !email ||
          !validateEmail(email) ||
          !billingName ||
          !address ||
          !validateAddress(address) ||
          !address.address_1,
      }}
      onClickConfirm={handleConnect}
    >
      <div className="flex flex-col w-full py-4 items-center space-y-4 px-8">
        <div className="flex space-x-6 items-center justify-center w-full">
          <Avatar
            className="cursor-pointer [&&]:w-[90px] [&&]:h-[90px] [&&]:rounded-2xl [&&]:border [&&]:border-grey-100 [&&]:bg-grey-50"
            variant="rounded"
          >
            <Business className="[&&]:w-[90px] [&&]:h-[90px] text-grey-600" />
          </Avatar>
          <SyncAlt className="[&&]:w-[70px] [&&]:h-[70px] text-grey-300" />
          <Avatar
            className="cursor-pointer [&&]:w-[90px] [&&]:h-[90px] [&&]:rounded-2xl [&&]:border [&&]:border-grey-100 [&&]:bg-grey-50"
            variant="rounded"
          >
            <img src={portexIcon} alt="Portex" className="w-[90px] h-[90px]" />
          </Avatar>
        </div>

        <Text size="medium">{t("common:smc3_login_credsPrompt")}</Text>
        {!!isCreated && (
          <Text size="small" typographyProps={{ color: "textSecondary" }}>
            {t("common:smc3_creds_alreadySaved")}
          </Text>
        )}

        {!!carrier?.creds_username_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text required={!isCreated} size="medium" weight="bold">
              {t("common:smc3_creds_username")}
            </Text>
            <TextInput value={username} onChange={(e) => setState({ username: e.target.value })} />
          </div>
        )}
        {!!carrier?.creds_password_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text required={!isCreated} size="medium" weight="bold">
              {t("common:smc3_creds_password")}
            </Text>
            <TextInput type="password" value={password} onChange={(e) => setState({ password: e.target.value })} />
          </div>
        )}
        {!!carrier?.creds_security_code_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text size="medium" weight="bold">
              {t("common:smc3_creds_securityCode")}
            </Text>
            <TextInput value={securityCode} onChange={(e) => setState({ securityCode: e.target.value })} />
          </div>
        )}
        {!!carrier?.creds_key_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text size="medium" weight="bold">
              {t("common:smc3_creds_key")}
            </Text>
            <TextInput value={key} onChange={(e) => setState({ key: e.target.value })} />
          </div>
        )}
        {!!carrier?.creds_secret_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text size="medium" weight="bold">
              {t("common:smc3_creds_secret")}
            </Text>
            <TextInput value={key} onChange={(e) => setState({ secret: e.target.value })} />
          </div>
        )}
        {!!carrier?.creds_tracking_secret_required && (
          <div className="flex flex-row w-full items-center justify-between">
            <Text size="medium" weight="bold">
              {t("common:smc3_creds_trackingSecret")}
            </Text>
            <TextInput value={key} onChange={(e) => setState({ trackingSecret: e.target.value })} />
          </div>
        )}

        <Text size="medium">{t("common:smc3_login_contact", { carrier: carrier?.carrier.name })}</Text>

        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold">
            {t("shipper:firstName")}
          </Text>
          <TextInput value={firstName} onChange={(e) => setState({ firstName: e.target.value })} />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold">
            {t("shipper:lastName")}
          </Text>
          <TextInput value={lastName} onChange={(e) => setState({ lastName: e.target.value })} />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text required size="medium" weight="bold">
            {t("common:emailAddress")}
          </Text>
          <TextInput value={email} onChange={(e) => setState({ email: e.target.value })} />
        </div>

        <Text size="medium">{t("common:smc3_billTo_header")}</Text>

        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold" required>
            {t("common:paymentTerms")}
          </Text>
          <FormSelectView<PaymentTerms>
            items={["COLLECT", "PREPAID", "THIRD_PARTY"]}
            getItemCopy={(item) =>
              item === "COLLECT"
                ? t("common:collect")
                : item === "PREPAID"
                ? t("common:prepaid")
                : t("common:thirdParty")
            }
            value={paymentTerms}
            onChange={(value) => setState({ paymentTerms: value })}
            required
            fullWidth
          />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold" required>
            {t("common:payer")}
          </Text>
          <FormSelectView<PaymentPayer>
            items={["CONSIGNEE", "SHIPPER", "THIRD_PARTY"]}
            getItemCopy={(item) =>
              item === "CONSIGNEE"
                ? t("common:collect")
                : item === "SHIPPER"
                ? t("common:shipper")
                : t("common:thirdParty")
            }
            value={paymentPayer}
            onChange={(value) => setState({ paymentPayer: value })}
            required
            fullWidth
          />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold">
            {t("common:currency")}
          </Text>
          <FormSelectView<Smc3Currency>
            items={["CAD", "MXN", "USD"]}
            getItemCopy={(item) => item}
            value={paymentCurrency}
            onChange={(value) => setState({ paymentCurrency: value })}
            fullWidth
            placeholder="USD"
          />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text size="medium" weight="bold">
            {t("common:accountNumber")}
          </Text>
          <TextInput
            value={billToAccountNumber}
            onChange={(e) => setState({ billToAccountNumber: e.target.value })}
            helperText="Required for contract pricing"
          />
        </div>
        <div className="flex flex-row w-full items-center justify-between">
          <Text required size="medium" weight="bold">
            {t("common:billingName")}
          </Text>
          <TextInput value={billingName} onChange={(e) => setState({ billingName: e.target.value })} />
        </div>
        <div className="flex flex-col w-full items-start justify-start">
          <Text required size="medium" weight="bold">
            {t("common:billingDetails")}
          </Text>
          <AddressForm
            mode={Mode.Ftl}
            address={address}
            onChange={(address) => setState({ address })}
            isSubmitting={isLoadingAddress}
            showActionButtons={false}
            showAddressType={false}
            showBookingNotes={false}
            showHoursOfOperation={false}
            showLocationName={false}
            LocationPickerProps={{ exactAddressRequired: true }}
          />
        </div>
      </div>
    </PortexDialog>
  );
};

export default CarrierIntegrationDialogContainer;
